import { createColorTransform } from '@limonade/color-science';
import { frameToImageData, SceneAwarePCEAnalyzer, pcePaletteReferenceMetadata, } from '@limonade/pce-core';
import { PCEExportManager, LUTGenerator, applyTargetedColorMatchesToRGB } from './index.js';
import { CompiledCubeLUT, createCubeLUT, applyCubeLUTToFrame } from './CubeLUT.js';
function clipping(frame) {
    let count = 0;
    let total = 0;
    for (let i = 0; i < frame.data.length; i += 4) {
        if (!frame.data[i + 3])
            continue;
        total++;
        if (Math.max(frame.data[i], frame.data[i + 1], frame.data[i + 2]) >= 254)
            count++;
    }
    return total ? count / total * 100 : 0;
}
/** Bounded feedback: every candidate is measured after applying the actual CUBE table. */
export class PCEMatchAudit {
    analyzerOptions;
    constructor(analyzerOptions = {}) {
        this.analyzerOptions = analyzerOptions;
    }
    async analyze(input, options = {}) {
        const size = options.size ?? 17;
        const requestedPasses = options.maxPasses ?? 2;
        const threshold = options.minimumImprovement ?? 0.002;
        if (!Number.isInteger(requestedPasses) || requestedPasses < 1 || requestedPasses > 3)
            throw new Error('PCE audit supports one to three passes.');
        if (!Number.isFinite(threshold) || threshold < 0)
            throw new Error('PCE audit improvement threshold must be a finite positive number.');
        const [reference, source] = await Promise.all([frameToImageData(input.referenceFrame), frameToImageData(input.sourceFrame)]);
        const warnings = [];
        let semanticContext = input.semanticContext;
        if (!input.options?.palettePresetId && !semanticContext && this.analyzerOptions.semanticSegmentationProvider) {
            options.onProgress?.('Detecting faces, people, and skin locally…');
            const segmentations = await Promise.allSettled([
                this.analyzerOptions.semanticSegmentationProvider.segment(reference),
                this.analyzerOptions.semanticSegmentationProvider.segment(source),
            ]);
            semanticContext = {};
            for (const [index, key] of ['reference', 'source'].entries()) {
                const segmentation = segmentations[index];
                if (segmentation.status === 'fulfilled')
                    semanticContext[key] = segmentation.value;
                else
                    warnings.push(`Local ${key} detection failed; deterministic fallback used: ${segmentation.reason instanceof Error ? segmentation.reason.message : String(segmentation.reason)}`);
            }
        }
        let paletteCorrection = input.options?.paletteCorrection;
        const compare = async (frame) => new SceneAwarePCEAnalyzer().analyze({
            ...input, referenceFrame: reference, sourceFrame: frame,
            semanticContext,
            options: { ...input.options, paletteCorrection, temporalStability: false },
        });
        const baseline = await compare(source);
        paletteCorrection = baseline.suggestedCorrections.paletteCorrection;
        let best = baseline;
        let correctedSource = source;
        let cube = createCubeLUT((rgb) => rgb, size, 'pre match audited');
        let firstCube = cube;
        const stages = [];
        const passes = [];
        const originalClipping = clipping(source);
        const exporter = new PCEExportManager();
        const sourceSpace = baseline.colorManagement.inputColorSpace.source;
        const workingSpace = baseline.colorManagement.workingColorSpace;
        let toWorking;
        let toSource;
        const workingDomain = workingSpace === 'OKLab-analysis-from-sRGB' ? 'sRGB' : workingSpace;
        if (workingDomain !== sourceSpace || input.options?.sourceInputTransferFunction) {
            toWorking = createColorTransform({ from: sourceSpace, to: workingDomain, inputTransferFunction: input.options?.sourceInputTransferFunction });
            toSource = createColorTransform({ from: workingDomain, to: sourceSpace, outputTransferFunction: input.options?.sourceInputTransferFunction });
            warnings.push(...toWorking.warnings, ...toSource.warnings);
        }
        if (baseline.colorManagement.transformWarnings.length)
            warnings.push(...baseline.colorManagement.transformWarnings);
        const sourceDomainCorrection = (suggestion) => toWorking && toSource
            ? new CompiledCubeLUT(createCubeLUT((rgb) => {
                const working = toWorking.rgb({ r: rgb[0], g: rgb[1], b: rgb[2] });
                const matched = suggestion.rgb([working.r, working.g, working.b]);
                const output = toSource.rgb({ r: matched[0], g: matched[1], b: matched[2] });
                return [output.r, output.g, output.b];
            }, size))
            : suggestion;
        for (let pass = 1; pass <= requestedPasses; pass++) {
            if (best.overallMatchScore >= 0.995 && !(pass === 1 && paletteCorrection?.monochrome))
                break;
            options.onProgress?.(`Audit pass ${pass}/${requestedPasses}: testing the exported LUT…`);
            await new Promise((resolve) => setTimeout(resolve, 0));
            const suggestion = new CompiledCubeLUT(exporter.generateCDLCubeLUT(best, {
                size,
                // A manual RGB target is in the original source domain and is applied once below.
            }));
            const measure = async (correction) => {
                const candidateStages = [...stages, correction];
                const candidateCube = createCubeLUT((rgb) => candidateStages.reduce((value, stage) => stage.rgb(value), rgb), size, 'pre match audited');
                const candidateFrame = applyCubeLUTToFrame(source, new CompiledCubeLUT(candidateCube));
                const candidateResult = await compare(candidateFrame);
                const improvement = candidateResult.overallMatchScore - best.overallMatchScore;
                const clipped = clipping(candidateFrame);
                const skinBefore = best.regionScores.skin;
                const skinAfter = candidateResult.regionScores.skin;
                const skinWorse = input.options?.protectSkin && skinBefore !== undefined && skinAfter !== undefined && skinAfter < skinBefore - 0.015;
                const clipWorse = clipped > originalClipping + 1;
                const toneWorse = best.tonalAnalysis?.bands.some((before) => {
                    const after = candidateResult.tonalAnalysis?.bands.find((band) => band.range === before.range);
                    if (before.confidence >= 0.25 && (!after || after.source.effectiveSampleCount < 8))
                        return true;
                    return before.confidence >= 0.25 && after && after.confidence >= 0.25 &&
                        before.matchScore !== null && after.matchScore !== null && after.matchScore < before.matchScore - 0.03;
                }) ?? false;
                const explicitMonochrome = Boolean(pass === 1 && paletteCorrection?.monochrome);
                const accepted = (improvement >= threshold || explicitMonochrome) && !skinWorse && !clipWorse && !toneWorse;
                const reason = skinWorse ? 'Rejected: protected skin match worsened.'
                    : clipWorse ? 'Rejected: highlight clipping increased by more than one percentage point.'
                        : toneWorse ? 'Rejected: a reliable tonal-range match worsened by more than three percentage points.'
                            : accepted ? explicitMonochrome ? 'Accepted: explicit zero-saturation preset; actual CUBE verified without increased clipping.' : 'Accepted: measured match improved.'
                                : 'Rejected: no reliable improvement; retained the previous best LUT.';
                return { correction, cube: candidateCube, frame: candidateFrame, result: candidateResult, improvement, clipped, accepted, reason };
            };
            let candidate = await measure(sourceDomainCorrection(suggestion));
            let strategy = 'combined';
            let correctionStrength = 1;
            const candidates = [];
            const record = (value, kind, strength) => {
                candidates.push({ strategy: kind, strength, matchScore: value.result.overallMatchScore, accepted: value.accepted, reason: value.reason });
            };
            record(candidate, strategy, 1);
            let combinedCandidate;
            let tonalCandidate;
            if (!candidate.accepted && best.suggestedCorrections.tonalCorrection) {
                // If global CDL would hurt an already-good range, test the tonal hue layer alone.
                // One extra bounded candidate, subject to exactly the same actual-CUBE safeguards.
                combinedCandidate = { matchScore: candidate.result.overallMatchScore, reason: candidate.reason };
                options.onProgress?.(`Audit pass ${pass}/${requestedPasses}: testing a safer tonal-only correction…`);
                await new Promise((resolve) => setTimeout(resolve, 0));
                const tonalOnly = new CompiledCubeLUT(new LUTGenerator().generateCubeFromCDL({
                    slope: [1, 1, 1], offset: [0, 0, 0], power: [1, 1, 1], saturation: 1,
                }, { size, tonalCorrection: best.suggestedCorrections.tonalCorrection }));
                const fallback = await measure(sourceDomainCorrection(tonalOnly));
                record(fallback, 'tonal-only', 1);
                tonalCandidate = { matchScore: fallback.result.overallMatchScore, accepted: fallback.accepted, reason: fallback.reason };
                if (fallback.accepted) {
                    candidate = fallback;
                    strategy = 'tonal-only';
                    candidate.reason = 'Accepted: tonal-only fallback improved the match without the rejected global CDL changes.';
                }
            }
            if (!candidate.accepted) {
                // Full-strength layers can fight each other: a global saturation/exposure
                // move may undo a good skin correction. Bounded component/strength search
                // retains ALL safeguards and measures the actual composed CUBE each time.
                const alternatives = [
                    ...[0.5, 0.25].map((strength) => ({
                        lut: new CompiledCubeLUT(createCubeLUT((rgb) => {
                            const matched = suggestion.rgb(rgb);
                            return rgb.map((value, i) => value + (matched[i] - value) * strength);
                        }, size)), strategy: 'combined', strength,
                    })),
                    { lut: new CompiledCubeLUT(new LUTGenerator().generateCubeFromCDL(exporter.generateCDL(best), { size })), strategy: 'cdl-only', strength: 1 },
                ];
                const skin = best.suggestedCorrections.skinToneMatch;
                if (skin && skin.confidence >= 0.25 && input.options?.matchMidtoneSkin !== false) {
                    alternatives.push({ lut: new CompiledCubeLUT(new LUTGenerator().generateCubeFromCDL({
                            slope: [1, 1, 1], offset: [0, 0, 0], power: skin.channelPower, saturation: 1,
                        }, { size })), strategy: 'skin-only', strength: 1 });
                }
                for (const alternative of alternatives) {
                    options.onProgress?.(`Audit pass ${pass}/${requestedPasses}: checking a bounded ${alternative.strategy} correction…`);
                    await new Promise((resolve) => setTimeout(resolve, 0));
                    const measured = await measure(sourceDomainCorrection(alternative.lut));
                    record(measured, alternative.strategy, alternative.strength);
                    if (measured.accepted && (!candidate.accepted || measured.result.overallMatchScore > candidate.result.overallMatchScore)) {
                        candidate = measured;
                        strategy = alternative.strategy;
                        correctionStrength = alternative.strength;
                    }
                }
            }
            if (pass === 1)
                firstCube = candidate.cube;
            passes.push({
                pass, accepted: candidate.accepted, matchScore: candidate.result.overallMatchScore,
                improvement: Number(candidate.improvement.toFixed(4)), clippedPixelPercentage: Number(candidate.clipped.toFixed(3)),
                reason: candidate.reason, strategy, correctionStrength, candidates, combinedCandidate, tonalCandidate,
                tonalMatchScores: candidate.result.tonalAnalysis ? Object.fromEntries(candidate.result.tonalAnalysis.bands.map((band) => [band.range, band.matchScore])) : undefined,
            });
            if (!candidate.accepted)
                break;
            stages.push(candidate.correction);
            best = candidate.result;
            correctedSource = candidate.frame;
            cube = candidate.cube;
        }
        if (options.targetedMatches?.length && !paletteCorrection?.monochrome) {
            // Manual picks are explicit user intent; preserve them while measuring their impact.
            const baseCube = new CompiledCubeLUT(cube);
            const targetedCube = createCubeLUT((rgb) => applyTargetedColorMatchesToRGB(baseCube.rgb(rgb), options.targetedMatches), size, 'pre match audited target');
            const targetedFrame = applyCubeLUTToFrame(source, new CompiledCubeLUT(targetedCube));
            const targetedResult = await compare(targetedFrame);
            if (targetedResult.overallMatchScore < best.overallMatchScore)
                warnings.push('The requested manual target correction lowered the global match score; inspect the preview.');
            cube = targetedCube;
            correctedSource = targetedFrame;
            best = targetedResult;
        }
        if (options.targetedMatches?.length && paletteCorrection?.monochrome)
            warnings.push('Manual color targets are ignored for Black & White so zero saturation is preserved.');
        if (!stages.length && !options.targetedMatches?.length) {
            warnings.push('No automatic correction improved the audited score; the best export is an identity LUT.');
            warnings.push(...passes.flatMap((pass) => pass.candidates?.map((candidate) => candidate.reason) ?? [pass.reason]));
        }
        else if (passes.some((pass) => pass.accepted && (pass.strategy !== 'combined' || pass.correctionStrength !== 1))) {
            warnings.push('A bounded correction was used because the full combined correction failed the audit. Match is partial; inspect the preview.');
        }
        warnings.push(...best.warnings);
        return {
            baselineResult: baseline, result: best, correctedSource, firstCube, cube, semanticContext,
            report: {
                schema: 'limonade-pce-match-audit/v1',
                scoreKind: baseline.paletteAnalysis ? 'palette-adherence' : 'scene-similarity',
                referencePreset: input.options?.palettePresetId ? pcePaletteReferenceMetadata(input.options.palettePresetId) : undefined,
                beforeMatchScore: baseline.overallMatchScore, bestMatchScore: best.overallMatchScore,
                improvement: Number((best.overallMatchScore - baseline.overallMatchScore).toFixed(4)),
                acceptedPasses: stages.length, passes,
                lut: { size, inputColorSpace: sourceSpace, outputColorSpace: sourceSpace, interpolation: 'trilinear' },
                colorManagement: best.colorManagement, semanticAnalysis: best.semanticAnalysis,
                matchingConfiguration: {
                    referenceMode: paletteCorrection ? 'creative-palette' : 'image-reference',
                    perceptualMetric: 'OKLab', ignoreGreenscreen: !paletteCorrection && Boolean(input.options?.ignoreGreenscreen), protectSkin: !paletteCorrection && Boolean(input.options?.protectSkin),
                    matchMidtoneSkin: !paletteCorrection && input.options?.matchMidtoneSkin !== false, matchTonalRanges: !paletteCorrection && Boolean(input.options?.matchTonalRanges),
                    matchSixVectorColors: !paletteCorrection && input.options?.matchSixVectorColors !== false, quality: input.options?.sampling?.quality ?? 'quality', lutSize: size, maxPasses: requestedPasses,
                },
                sixVectorMatch: best.suggestedCorrections.colorVectorMatch,
                beforeTonalAnalysis: baseline.tonalAnalysis, tonalAnalysis: best.tonalAnalysis,
                warnings: [...new Set(warnings)],
                limitations: [
                    'Scores are perceptual heuristics; different scenes do not establish object correspondence.',
                    'Local face/person masks may miss distant, occluded, or strongly lit subjects.',
                    'A global LUT cannot isolate a particular person or object; masks guide analysis only.',
                    'Tonal masks use display-normalized luminance, not recovered scene exposure; letterbox borders and different compositions can bias statistics.',
                    'Preview uses trilinear interpolation; other grading applications may interpolate differently.',
                    'Camera log profiles remain metadata-only unless an exact transform is implemented.',
                ],
            },
        };
    }
}
